import React from "react";
import { TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { IconV2 } from "components/V2/atoms/IconV2/IconV2";
import { IconBtn } from "../../../atom/IconBtn/IconBtn";
import { validateEmail } from "../../../constants/functions";
import { Cross } from "../../../constants/icons";
import {
  DEBOUNCE_DELAY,
  FORGOT_PASSWORD_MODULE,
  SUCCESS_AUTH,
  SIGN_IN_LANDING_PAGE
} from "../../../constants/variables";
import { useAuth } from "../../../context/AuthContext";
import { extractResource } from "../../../helper/extractResource";
import { Button } from "../../UI/Button";

function SignInWithPassword(props) {
  const [values, setValues] = React.useState({
    emailOrMobile: "",
    password: "",
    showPassword: false
  });
  const [timerId, setTimerId] = React.useState<any>(null);
  const [error, setError] = React.useState(false);
  const [wrongPass, setWrongPass] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const { login } = useAuth();
  const { resourceData } = props;

  // ? the function toggles between hide and show password
  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword
    });
  };

  // ? on mouseDown it shifts the focus away from input fields - From MUI
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // ? the function validates email or mobile number
  // ? sets errors if invalid
  const main = (e: any) => {
    if (e.target.name === "emailOrMobile") {
      if (validateEmail(e.target.value)) setError(false);
      else setError(true);
    }
  };

  // ? the function updates the emailOrMobile state with current input value
  // ? it also triggers the debounce function
  const handleChange = (e: any) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value
    });

    debounce(e, DEBOUNCE_DELAY);
  };

  // ? the debounce function calls the main fuction 1s after user stops typing
  const debounce = (e: any, delay: any) => {
    if (e.target.name === "emailOrMobile") setError(false);
    if (e.target.name === "password") setWrongPass(false);

    if (timerId) {
      clearTimeout(timerId);
      setTimerId(null);
    }

    setTimerId(
      setTimeout(() => {
        main(e);
      }, delay)
    );
  };

  /**  the function validates user credentials number before submission
   *   it sets errors if invalid
   *    if valid, it allows the action call and resets the form data
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true); // begin the loading animation
    setError(false);

    if (validateEmail(values.emailOrMobile)) {
      // dummy password authentication
      await login(values.emailOrMobile, values.password).then((resp) => {
        if (resp) {
          setWrongPass(false);
          props.handleState(SUCCESS_AUTH); // redirect to success page
        } else setWrongPass(true);
      });
      // let loginResp = true;
    } else {
      setError(true);
    } // if it is not a valid email or a number, set the error to true

    setIsLoading(false); // end animation if submit data is invalid
  };

  return (
    <section className="w-full sm:w-80 m-auto h-full flex flex-col justify-between">
      <div className="w-full h-full">
        <div className="flex justify-between items-center text-typography-primary">
          <h1 className="font-semibold text-xl">{extractResource(resourceData, "signInWithPassword")}</h1>
          <IconBtn
            onClick={() => {
              // closes the drawer
              props.handleOpen(false);
            }}
            aria-label={extractResource(resourceData, "goBack")}
            title={extractResource(resourceData, "goBack")}
            type="button"
            edge="start"
          >
            <Cross />
          </IconBtn>
        </div>

        <form onSubmit={handleSubmit} name="signInWithPassword" className="space-y-4 mt-6">
          <TextField
            error={error}
            label={extractResource(resourceData, "emailId")}
            value={values.emailOrMobile}
            helperText={error ? extractResource(resourceData, "invalidEmail") : ""}
            onChange={handleChange}
            name="emailOrMobile"
            fullWidth
            required
          />

          <FormControl variant="outlined" fullWidth required error={wrongPass}>
            <InputLabel htmlFor="outlined-adornment-password">{extractResource(resourceData, "password")}</InputLabel>
            <OutlinedInput
              id="password"
              type={values.showPassword ? "text" : "password"}
              value={values.password}
              onChange={handleChange}
              name="password"
              endAdornment={
                <InputAdornment position="end">
                  <IconBtn
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    color="primary"
                  >
                    {values.showPassword ? (
                      <IconV2
                        blok={{
                          component: "icon",
                          icon: "visibility-outlined",
                          height: 28,
                          width: 26,
                          alt: "visibility Outlined"
                        }}
                      />
                    ) : (
                      <IconV2
                        blok={{
                          component: "icon",
                          icon: "visibility-off-outlined",
                          height: 28,
                          width: 26,
                          alt: "visibility Off Outlined"
                        }}
                      />
                    )}
                  </IconBtn>
                </InputAdornment>
              }
              label={extractResource(resourceData, "password")}
              fullWidth
            />
            <p className="text-typography-error text-xs my-2 mx-3">
              {wrongPass && extractResource(resourceData, "invalidLoginCredentials")}
            </p>
          </FormControl>

          <Button
            fullWidth
            color="primary"
            type="submit"
            variant="contained"
            isLoading={isLoading}
            aria-label={extractResource(resourceData, "signIn")}
          >
            {extractResource(resourceData, "signIn")}
          </Button>
        </form>

        <Button
          className="w-80 px-6 my-5"
          color="secondary"
          type="button"
          variant="text"
          onClick={() => {
            props.handleModule(FORGOT_PASSWORD_MODULE);
          }}
        >
          {extractResource(resourceData, "forgotPassword")}
        </Button>
      </div>
      <div className="w-full text-center relative bottom-0 right-0">
        <Button
          className="w-80 px-6 my-5"
          color="secondary"
          type="button"
          variant="text"
          onClick={() => {
            props.handleState(SIGN_IN_LANDING_PAGE);
          }}
        >
          {extractResource(resourceData, "otherSignInOptions")}
        </Button>
      </div>
    </section>
  );
}

export default SignInWithPassword;
